import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { NavIconBump, NavActivePill } from './nav/NavTabPolish';
import { useScrollNavCompact } from '../hooks/useScrollNavCompact';
import { jumpToTop } from '../utils/jumpToTop';
import UnreadBadge from './UnreadBadge';
import { NavProfileAvatar } from './nav/NavProfileAvatar';
import { tapVibrate } from '../utils/hapticFeedback';
import { useVenueActionNeeded } from '../hooks/useVenueActionNeeded';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const NOTIF_POLL_INTERVAL = 12000;

const ACTIVE_COLOR = 'var(--accent-purple)';

// People/Staff icon (exact SVG from Figma)
const PeopleIcon = ({ color }) => (
  <svg width="30" height="26" viewBox="0 0 30 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M28.125 26C28.125 26 30 26 30 23.8333C30 21.6667 28.125 15.1667 20.625 15.1667C13.125 15.1667 11.25 21.6667 11.25 23.8333C11.25 26 13.125 26 13.125 26H28.125ZM13.1669 23.8333C13.1604 23.8325 13.1513 23.8311 13.1403 23.829C13.1351 23.828 13.13 23.8269 13.125 23.8258C13.1277 23.2537 13.4379 21.595 14.5486 20.0975C15.5875 18.6969 17.405 17.3333 20.625 17.3333C23.845 17.3333 25.6625 18.6969 26.7014 20.0975C27.8121 21.595 28.1223 23.2537 28.125 23.8258C28.12 23.8269 28.1149 23.828 28.1097 23.829C28.0987 23.8311 28.0896 23.8325 28.0831 23.8333H13.1669Z" fill={color}/>
    <path d="M20.625 10.8333C22.6961 10.8333 24.375 8.89323 24.375 6.5C24.375 4.10677 22.6961 2.16667 20.625 2.16667C18.5539 2.16667 16.875 4.10677 16.875 6.5C16.875 8.89323 18.5539 10.8333 20.625 10.8333ZM26.25 6.5C26.25 10.0899 23.7316 13 20.625 13C17.5184 13 15 10.0899 15 6.5C15 2.91015 17.5184 0 20.625 0C23.7316 0 26.25 2.91015 26.25 6.5Z" fill={color}/>
    <path d="M13.0049 15.7733C12.3152 15.5184 11.5491 15.3331 10.6993 15.2383C10.2787 15.1914 9.83753 15.1667 9.375 15.1667C1.875 15.1667 0 21.6667 0 23.8333C0 25.2778 0.625 26 1.875 26H9.78068C9.51668 25.3845 9.375 24.6548 9.375 23.8333C9.375 21.6444 10.0823 19.409 11.4184 17.5417C11.8749 16.9038 12.4047 16.3088 13.0049 15.7733ZM9.22508 17.3343C8.10479 19.3128 7.5 21.5685 7.5 23.8333H1.875C1.875 23.2684 2.18296 21.6016 3.29861 20.0975C4.32128 18.7188 6.09842 17.376 9.22508 17.3343Z" fill={color}/>
    <path d="M2.8125 7.58333C2.8125 3.99348 5.3309 1.08333 8.4375 1.08333C11.5441 1.08333 14.0625 3.99348 14.0625 7.58333C14.0625 11.1732 11.5441 14.0833 8.4375 14.0833C5.3309 14.0833 2.8125 11.1732 2.8125 7.58333ZM8.4375 3.25C6.36643 3.25 4.6875 5.1901 4.6875 7.58333C4.6875 9.97657 6.36643 11.9167 8.4375 11.9167C10.5086 11.9167 12.1875 9.97657 12.1875 7.58333C12.1875 5.1901 10.5086 3.25 8.4375 3.25Z" fill={color}/>
  </svg>
);

// Heartbeat/Analytics icon (exact SVG from Figma)
const AnalyticsIcon = ({ color }) => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0.75 13.1299H7.25L10.5657 0.75L17.712 26.75L20.2342 13.1299H26.75" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// QR Scan icon (exact SVG from Figma)
const ScanIcon = ({ color }) => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.65888 14H21.3412M20.8824 1H23.9412C25.6305 1 27 2.36948 27 4.05882V7.11765M20.8824 27H23.9412C25.6305 27 27 25.6305 27 23.9412V20.8824M7.11765 1L4.05882 1C2.36948 1 1 2.36948 1 4.05882L1 7.11765M7.11765 27H4.05882C2.36948 27 1 25.6305 1 23.9412V20.8824" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Messenger/Send icon (exact SVG from Figma)
const MessengerIcon = ({ color }) => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.00795 14.9248L8.72209 16.5127C9.14037 16.6518 9.53176 16.8507 9.88475 17.1006C10.1116 17.2611 10.3226 17.4427 10.5146 17.6432C10.9536 18.1012 11.2937 18.6573 11.4985 19.2843L13.0892 23.9903C14.4485 28.0611 20.1749 27.9746 21.4474 23.9037L26.7978 6.72535C27.3167 5.02742 26.7939 3.38686 25.72 2.30049C24.6392 1.20714 23.0001 0.675124 21.3027 1.21096L4.06579 6.55213C0.0168344 7.82245 -0.0410024 13.5678 4.00795 14.9248Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10.0429 16.5434C10.5143 17.0148 10.9857 17.4862 11.4571 17.9576C11.6945 17.6966 11.9319 17.4357 12.1693 17.1747C13.7896 15.3935 15.41 13.6122 17.0304 11.8309C19.6829 8.91505 22.3354 5.9992 24.9879 3.08334C25.2252 2.82239 25.4626 2.56144 25.7 2.30049C25.439 2.53787 25.1781 2.77525 24.9171 3.01263C22.0013 5.66511 19.0854 8.31759 16.1696 10.9701C14.3883 12.5905 12.607 14.2108 10.8257 15.8312C10.5648 16.0686 10.3038 16.306 10.0429 16.5434Z" fill={color}/>
  </svg>
);

// Profile avatar
const ProfileIcon = ({ isActive }) => (
  <NavProfileAvatar isActive={isActive} />
);

const tabs = [
  { id: 'people', path: '/venue/people', label: 'People' },
  { id: 'analytics', path: '/venue/analytics', label: 'Analytics' },
  { id: 'scan', path: '/venue/scan', label: 'Scan' },
  { id: 'messages', path: '/venue/share', label: 'Messages' },
  { id: 'profile', path: '/venue/profile', label: 'Profile' },
];

// Hook: poll /api/messages/conversations for unread chat-message count.
// Used to drive the red badge on the Messages tab. Falls back silently
// when there is no auth token or active venue (e.g. during logout).
const useUnreadCount = () => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      const token = (typeof window !== 'undefined') ? window.localStorage.getItem('token') : null;
      if (!token) return;
      const activeVenueAppId = (typeof window !== 'undefined') ? window.localStorage.getItem('activeVenueAppId') : null;
      try {
        const headers = { Authorization: `Bearer ${token}` };
        if (activeVenueAppId) headers['X-Active-Venue-App-Id'] = activeVenueAppId;
        // Match the venue messages page so the badge counts only venue-scope
        // conversations (matches backend X-Active-Role filter).
        headers['X-Active-Role'] = 'venue';
        const res = await axios.get(`${API}/messages/conversations`, { headers });
        const conversations = Array.isArray(res.data) ? res.data : [];
        const total = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
        if (!cancelled) setCount(total);
      } catch (_) { /* swallow */ }
    };
    fetchCount();
    const interval = window.setInterval(fetchCount, NOTIF_POLL_INTERVAL);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, []);
  return count;
};

// Pill badge rendered over the Messages icon.
// Shared <UnreadBadge/> is imported from ../components/UnreadBadge.

export const VenueBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const DEFAULT_COLOR = isDark ? '#9A9AA5' : '#404040';
  // Chat threads (e.g. /venue/chat/abc) live under the Messages tab,
  // so map them to 'messages' explicitly. Same for any future sub-route that
  // doesn't directly start with one of the canonical tab paths.
  const resolveActiveTab = () => {
    const direct = tabs.find(t => location.pathname.startsWith(t.path));
    if (direct) return direct.id;
    if (location.pathname.startsWith('/venue/chat')) return 'messages';
    if (location.pathname.startsWith('/venue/messages')) return 'messages';
    if (location.pathname.startsWith('/venue/notifications')) return 'profile';
    if (location.pathname.startsWith('/venue/settings')) return 'profile';
    if (location.pathname.startsWith('/venue/awaiting-payment')) return 'scan';
    if (location.pathname.startsWith('/venue/transaction')) return 'scan';
    return 'analytics';
  };
  const activeTab = resolveActiveTab();
  const unreadCount = useUnreadCount();
  // Owner still has unfinished setup OR an unconnected payout method —
  // show a small red dot on the Scan and Analytics tabs so they can't miss
  // the CTAs no matter which screen they live on. Clears automatically the
  // moment the underlying state resolves.
  const { needsAction } = useVenueActionNeeded();

  const renderIcon = (tab) => {
    const isActive = activeTab === tab.id;
    const color = isActive ? ACTIVE_COLOR : DEFAULT_COLOR;
    switch (tab.id) {
      case 'people': return <PeopleIcon color={color} />;
      case 'analytics': return <AnalyticsIcon color={color} />;
      case 'scan': return <ScanIcon color={color} />;
      case 'messages': return <MessengerIcon color={color} />;
      case 'profile': return <ProfileIcon isActive={isActive} />;
      default: return null;
    }
  };

  const navCompact = useScrollNavCompact();

  return (
    <motion.nav
      className="fixed left-0 right-0 z-[9999]"
      animate={{ height: navCompact ? 58 : 72 }}
      transition={{ type: 'spring', stiffness: 320, damping: 32 }}
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)', pointerEvents: 'none' }}
    >
      <div className="mx-auto w-[calc(100%-24px)] max-w-[380px] h-full px-3" style={{ background: 'var(--nav-surface)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', borderRadius: 999, boxShadow: 'var(--nav-shadow)', border: '1px solid var(--card-border)', pointerEvents: 'auto' }}>
        <motion.div
          className="flex items-center justify-around h-full"
          animate={{ scale: navCompact ? 0.88 : 1 }}
          transition={{ type: 'spring', stiffness: 320, damping: 32 }}
          style={{ transformOrigin: 'center center' }}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  tapVibrate();
                  if (isActive && location.pathname === tab.path) {
                    if (jumpToTop()) return;
                  }
                  navigate(tab.path);
                }}
                data-testid={`venue-nav-${tab.id}`}
                aria-current={isActive ? 'page' : undefined}
                data-active={isActive ? 'true' : undefined}
                style={{
                  width: 48, height: 48, background: 'none', border: 'none',
                  cursor: 'pointer', padding: 0, position: 'relative',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <NavIconBump isActive={isActive}>
                  {renderIcon(tab)}
                </NavIconBump>
                {tab.id === 'messages' && <UnreadBadge count={unreadCount} dataTestId="venue-nav-unread-badge" />}
                {needsAction && (tab.id === 'scan' || tab.id === 'analytics') && (
                  <span
                    data-testid={`venue-nav-${tab.id}-action-dot`}
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: '#DC2626',
                      border: '2px solid var(--nav-surface)',
                      boxShadow: '0 0 0 1px rgba(220, 38, 38, 0.35)',
                    }}
                  />
                )}
                {isActive && (
                  <NavActivePill
                    layoutId="venue-bottom-nav-indicator"
                    dataTestId="venue-bottom-nav-active-indicator"
                  />
                )}
              </button>
            );
          })}
        </motion.div>
      </div>
    </motion.nav>
  );
};

export default VenueBottomNav;
